import {
  Button,
  Card,
  Modal,
  Pagination,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import PageTitle from '../../components/page-title';
import Filter from './component/filter';
import styles from './styles.module.scss';
import CustomButton from 'components/custom-button';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { shortenName } from 'utils/short-name';
import ModalCreateEdit from './component/ModalCreateEdit';
import moment from 'moment';
import dayjs from 'dayjs';
import { PK_NOTIFY_DEFAULT_GROUPS, PK_NOTIFY_GROUP_OPTIONS } from 'constants/pk-notify';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { pkErrorText } from './pk-error';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

/** Nhãn nhỏ dưới giờ bắt đầu: đã gửi / sẽ gửi thông báo khi trận bắt đầu. */
const NotifyStatus = ({ record }: { record: any }) => {
  if (record.startNotifiedAt) {
    return (
      <div className={styles.notifyTag}>
        <Tooltip
          title={`Đã gửi thông báo lúc ${dayjs(record.startNotifiedAt).format('HH:mm DD/MM')}`}
        >
          <Tag color="success">Đã báo</Tag>
        </Tooltip>
      </div>
    );
  }
  if (!record.notifyOnStart) return null;
  const groups: string[] = record.notifyGroups
    ? record.notifyGroups.split(',')
    : PK_NOTIFY_DEFAULT_GROUPS;
  const labels = PK_NOTIFY_GROUP_OPTIONS.filter((o) => groups.includes(o.value))
    .map((o) => o.label)
    .join(', ');
  return (
    <div className={styles.notifyTag}>
      <Tooltip title={`Báo khi trận bắt đầu tới: ${labels}`}>
        <Tag color="processing">Sẽ báo</Tag>
      </Tooltip>
    </div>
  );
};

// Giờ 24h kiểu Việt Nam; moment 'LT' ra "6:30 PM" và hay tách "AM/PM" xuống dòng riêng.
const formatDateTime = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY HH:mm') : '';

// Một dòng, dài quá thì "…" + tooltip đủ tên.
const renderUsername = (username?: string) => (
  <Typography.Text className={styles.cellText} ellipsis={{ tooltip: username }}>
    {username || ''}
  </Typography.Text>
);

export interface PKFilter {
  limit: number;
  page: number;
  startDate?: string;
  endDate?: string;
}

const PKManager = () => {
  // MOD (SMOD) được xếp/sửa trận; chỉ Admin được xoá (server: PK_DELETE chỉ Admin).
  const { isAdmin } = useCurrentRoles();
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as PKFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onFinish = (value: Partial<PKFilter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchPks = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.pks, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchPks();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      ellipsis: true,
      key: 'id',
    },
    {
      // Trường `name` của trận PK: tên nhóm quản lý (Agency) — xem modal tạo trận.
      title: 'Nhóm quản lý',
      dataIndex: 'name',
      key: 'name',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 60) || ''}</span>
        </Tooltip>
      ),
    },
    // {
    //   title: 'Ảnh',
    //   dataIndex: 'image',
    //   key: 'image',
    //   render: (value) => (
    //     <img
    //       width={136}
    //       height={72}
    //       style={{ objectFit: 'cover' }}
    //       src={`${SERVER_FILE_ENDPOINT}/${value}`}
    //       alt=""
    //     />
    //   ),
    // },
    {
      title: 'Creator 1',
      key: 'pk1',
      // Username không có dấu cách: để tự do thì ô ngắt giữa chữ ("mintdethuon" / "g623").
      width: 160,
      render: (_, record) => renderUsername(record?.pk1Info?.username || record?.pk1),
    },

    {
      title: 'Quốc gia 1',
      key: 'Country',
      width: 122,
      render: (_, record) => (
        <div>
          <div>{record?.setting1?.country}</div>
          <img
            src={`${SERVER_FILE_ENDPOINT}/${record?.setting1?.img}`}
            width={90}
            height={60}
            style={{ objectFit: 'cover' }}
            alt="img-country"
          />
        </div>
      ),
    },

    {
      title: 'Creator 2',
      key: 'pk2',
      width: 160,
      render: (_, record) => renderUsername(record?.pk2Info?.username || record?.pk2),
    },

    {
      title: 'Quốc gia 2',
      key: 'Country 2',
      width: 122,
      render: (_, record) => (
        <div>
          <div>{record?.setting2?.country}</div>
          <img
            src={`${SERVER_FILE_ENDPOINT}/${record?.setting2?.img}`}
            width={90}
            height={60}
            style={{ objectFit: 'cover' }}
            alt="img-country"
          />
        </div>
      ),
    },

    {
      title: 'Bắt đầu',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 164,
      // Trạng thái "thông báo khi trận bắt đầu" nằm ngay dưới giờ, không thêm cột
      // (bảng đã sát bề ngang ở màn ~1280–1366).
      render: (value, record) => (
        <>
          <span className={styles.nowrap}>{formatDateTime(value)}</span>
          <NotifyStatus record={record} />
        </>
      ),
    },

    // {
    //   title: 'Ngày kết thúc',
    //   dataIndex: 'endDate',
    //   key: 'endDate',
    //   render: (value) => (
    //     <span>
    //       {formatedDate(value)} - {moment(value).format('LT')}
    //     </span>
    //   ),
    // },
    {
      title: 'Thao tác',
      width: 104,
      key: 'action',
      // Bảng rộng hơn khung ở màn ~1280 (cuộn ngang) → ghim cột thao tác bên phải.
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deletePK, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: pkErrorText(error),
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="PK Đại chiến"
        extra={
          <Space wrap>
            {isAdmin && hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} trận
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm trận PK"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <div className={styles.filterContainer}>
        <Filter onSubmit={onFinish} />
      </div>
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          // Chọn nhiều chỉ để xoá → chỉ Admin thấy ô chọn.
          rowSelection={isAdmin ? rowSelection : undefined}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          // Bảng rỗng thì bỏ scroll.x: ô "Trống" rộng hơn khung 2px → cuộn ngang thừa.
          scroll={dataResponse.items?.length ? { x: 1112 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa trận PK"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} trận PK đã chọn?</p>
      </Modal>
    </div>
  );
};

export default PKManager;
